import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog } from '../../components/ui/dialog';
import { AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth-store';
import { useForm } from 'react-hook-form';
import { useUpdateUser } from '../../hooks/use-users';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';
import { useState, useEffect } from 'react';

interface NotifPref {
  eventType: string;
  channel: string;
  enabled: boolean;
}

const EVENT_TYPES = [
  { key: 'TICKET_CREATED', label: 'Tichet nou creat' },
  { key: 'TICKET_ASSIGNED', label: 'Tichet atribuit' },
  { key: 'TICKET_STATUS_CHANGED', label: 'Status tichet schimbat' },
  { key: 'COMMENT_ADDED', label: 'Comentariu nou' },
  { key: 'SLA_BREACH', label: 'Breșă SLA' },
];

export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const { toast } = useToast();
  const updateUser = useUpdateUser();
  const qc = useQueryClient();

  const { register, handleSubmit } = useForm({ defaultValues: { name: user?.name ?? '' } });

  const onProfile = async (data: { name: string }) => {
    if (!user) return;
    try {
      await updateUser.mutateAsync({ id: user.id, name: data.name });
      toast('success', 'Profilul a fost salvat cu succes');
    } catch (e) {
      toast('error', `Eroare la salvare: ${(e as Error).message}`);
    }
  };

  const { data: prefsData } = useQuery({
    queryKey: ['notification-prefs'],
    queryFn: () => api.get<NotifPref[]>('/notifications/preferences'),
  });
  const prefs = prefsData?.data ?? [];

  const togglePref = useMutation({
    mutationFn: (p: { eventType: string; channel: string; enabled: boolean }) =>
      api.put('/notifications/preferences', [p]),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-prefs'] }),
  });

  // Business hours settings (ADMIN only)
  const { data: bhData } = useQuery({
    queryKey: ['business-hours'],
    queryFn: () => api.get<{ startHour: number; endHour: number }>('/settings/business-hours'),
    enabled: user?.role === 'ADMIN',
  });
  const [bhStart, setBhStart] = useState<string>('');
  const [bhEnd, setBhEnd] = useState<string>('');
  const bhMutation = useMutation({
    mutationFn: (data: { startHour?: number; endHour?: number }) =>
      api.patch('/settings/business-hours', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['business-hours'] });
      toast('success', 'Programul de lucru a fost salvat cu succes');
    },
    onError: (e) => toast('error', `Eroare la salvare: ${e.message}`),
  });

  // Escalation settings (ADMIN only)
  const { data: escData } = useQuery({
    queryKey: ['escalation-settings'],
    queryFn: () => api.get<{ recipients: string[]; enabled: boolean; minutesBefore: number }>('/settings/escalation'),
    enabled: user?.role === 'ADMIN',
  });
  const [escRecipients, setEscRecipients] = useState<string>('');
  const [escEnabled, setEscEnabled] = useState<boolean>(true);
  const [escMinutes, setEscMinutes] = useState<string>('10');
  const [escInitialized, setEscInitialized] = useState(false);

  // Confirmation dialog for global (system-wide) setting saves
  const [confirm, setConfirm] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  useEffect(() => {
    if (escData?.data && !escInitialized) {
      setEscRecipients(escData.data.recipients.join('\n'));
      setEscEnabled(escData.data.enabled);
      setEscMinutes(String(escData.data.minutesBefore));
      setEscInitialized(true);
    }
  }, [escData, escInitialized]);

  const escMutation = useMutation({
    mutationFn: (data: { recipients?: string[]; enabled?: boolean; minutesBefore?: number }) =>
      api.patch('/settings/escalation', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['escalation-settings'] });
      setEscInitialized(false);
      toast('success', 'Setările de escaladare au fost salvate cu succes');
    },
    onError: (e) => toast('error', `Eroare la salvare: ${e.message}`),
  });

  const isPrefEnabled = (eventType: string) => {
    const pref = prefs.find((p) => p.eventType === eventType && p.channel === 'EMAIL');
    return pref?.enabled ?? true;
  };

  // Teams is opt-in: no preference row means disabled.
  const isTeamsPrefEnabled = (eventType: string) => {
    const pref = prefs.find((p) => p.eventType === eventType && p.channel === 'TEAMS');
    return pref?.enabled ?? false;
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">Setări</h1>

      <section className="space-y-4 rounded-[1.75rem] border border-[color:var(--surface-line)] bg-[color-mix(in_oklab,var(--color-primary)_4%,var(--surface-canvas))] p-4 md:p-5">
        <header className="flex items-center justify-between gap-3 px-1">
          <div>
            <h2 className="text-lg font-semibold text-[color:var(--ink-strong)]">Profilul meu</h2>
            <p className="mt-1 text-xs text-[color:var(--ink-muted)]">Datele contului tău și preferințele de notificare. Vizibile doar ție.</p>
          </div>
          <Badge className="shrink-0 bg-[color-mix(in_oklab,var(--color-primary)_12%,transparent)] text-[color:var(--color-primary)]">Doar pentru tine</Badge>
        </header>

      <Card>
        <CardHeader><h2 className="font-semibold">Profil</h2></CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onProfile)} className="space-y-4">
            <Input label="Email" value={user?.email ?? ''} disabled />
            <Input {...register('name')} label="Nume" />
            <Input label="Rol" value={user?.role ?? ''} disabled />
            <div className="flex justify-end">
              <Button type="submit" disabled={updateUser.isPending}>Salvează</Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><h2 className="font-semibold">Notificări</h2></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-gray-500 dark:text-dark-muted">
            Alege canalele prin care primești alerte. Canalul Teams trimite un mesaj formatat cu detaliile tichetului și linkul către el (activare opțională, per eveniment).
          </p>
          {EVENT_TYPES.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-700 dark:text-dark-text">{label}</span>
              <div className="flex items-center gap-4">
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-500 dark:text-dark-muted">
                  <input
                    type="checkbox"
                    checked={isPrefEnabled(key)}
                    onChange={(e) => togglePref.mutate({ eventType: key, channel: 'EMAIL', enabled: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  Email
                </label>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-500 dark:text-dark-muted">
                  <input
                    type="checkbox"
                    checked={isTeamsPrefEnabled(key)}
                    onChange={(e) => togglePref.mutate({ eventType: key, channel: 'TEAMS', enabled: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  Teams
                </label>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      </section>

      {user?.role === 'ADMIN' && (
      <section className="space-y-4 rounded-[1.75rem] border border-[color-mix(in_oklab,var(--color-warning)_35%,var(--surface-line))] bg-[color-mix(in_oklab,var(--color-warning)_9%,var(--surface-canvas))] p-4 md:p-5">
        <header className="flex items-center justify-between gap-3 px-1">
          <div>
            <h2 className="text-lg font-semibold text-[color:var(--ink-strong)]">Setări globale</h2>
            <p className="mt-1 text-xs text-[color:var(--ink-muted)]">Ajustări care se aplică întregii aplicații, nu doar contului tău.</p>
          </div>
          <Badge className="flex shrink-0 items-center gap-1 bg-[color-mix(in_oklab,var(--color-warning)_22%,transparent)] text-[color:var(--color-accent-dark)] dark:text-[color:var(--color-warning)]">
            <AlertTriangle size={13} /> Afectează întreg sistemul
          </Badge>
        </header>

        <Card>
          <CardHeader><h2 className="font-semibold">Program de lucru (SLA)</h2></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-gray-500 dark:text-dark-muted">Timpii SLA se calculează doar în intervalul de lucru configurat (Luni–Vineri). Weekend-urile sunt excluse automat.</p>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="mb-1 block text-xs text-gray-500 dark:text-dark-muted">Ora de start</label>
                <Input
                  type="number"
                  min="0"
                  max="23"
                  value={bhStart || (bhData?.data?.startHour != null ? String(bhData.data.startHour) : '10')}
                  onChange={(e) => setBhStart(e.target.value)}
                  onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
                />
              </div>
              <div className="flex-1">
                <label className="mb-1 block text-xs text-gray-500 dark:text-dark-muted">Ora de sfârșit</label>
                <Input
                  type="number"
                  min="1"
                  max="24"
                  value={bhEnd || (bhData?.data?.endHour != null ? String(bhData.data.endHour) : '18')}
                  onChange={(e) => setBhEnd(e.target.value)}
                  onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                disabled={bhMutation.isPending}
                onClick={() =>
                  setConfirm({
                    title: 'Salvezi programul de lucru?',
                    message: 'Intervalul de lucru afectează calculul SLA pentru toate tichetele din sistem, nu doar pentru contul tău.',
                    onConfirm: () => {
                      const payload: { startHour?: number; endHour?: number } = {};
                      if (bhStart) payload.startHour = parseInt(bhStart, 10);
                      if (bhEnd) payload.endHour = parseInt(bhEnd, 10);
                      bhMutation.mutate(payload);
                    },
                  })
                }
              >
                Salvează
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><h2 className="font-semibold">Escaladare SLA (Teams)</h2></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-gray-500 dark:text-dark-muted">
              Când un SLA este pe punctul de a fi depășit, se trimite un mesaj urgent prin Microsoft Teams către persoanele de mai jos. Introduceți adresele de email (una per linie).
            </p>
            <label className="flex items-center justify-between">
              <span className="text-sm text-gray-700 dark:text-dark-text">Escaladare activă</span>
              <input
                type="checkbox"
                checked={escEnabled}
                onChange={(e) => setEscEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
            </label>
            <div>
              <label className="mb-1 block text-xs text-gray-500 dark:text-dark-muted">Minute înainte de limita SLA</label>
              <Input
                type="number"
                min="1"
                max="120"
                value={escMinutes}
                onChange={(e) => setEscMinutes(e.target.value)}
                onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500 dark:text-dark-muted">Destinatari escaladare (email-uri, unul pe linie)</label>
              <textarea
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-dark-border dark:bg-dark-card dark:text-dark-text"
                rows={4}
                placeholder={"email1@encorsa.ro\nemail2@encorsa.ro"}
                value={escRecipients}
                onChange={(e) => setEscRecipients(e.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <Button
                disabled={escMutation.isPending}
                onClick={() =>
                  setConfirm({
                    title: 'Salvezi setările de escaladare?',
                    message: 'Escaladarea SLA afectează întregul sistem: mesajele Teams vor fi trimise tuturor destinatarilor configurați, pentru orice tichet.',
                    onConfirm: () => {
                      const recipients = escRecipients
                        .split('\n')
                        .map((s) => s.trim())
                        .filter(Boolean);
                      escMutation.mutate({
                        recipients,
                        enabled: escEnabled,
                        minutesBefore: parseInt(escMinutes, 10) || 10,
                      });
                    },
                  })
                }
              >
                Salvează
              </Button>
            </div>
          </CardContent>
        </Card>
        <p className="px-1 text-xs text-[color:var(--ink-muted)]">
          Setările <Link to="/admin/webcon" className="font-medium text-primary hover:underline">WebCon</Link> și{' '}
          <Link to="/admin/microsoft" className="font-medium text-primary hover:underline">Microsoft</Link> se gestionează separat, dar afectează tot întregul sistem.
        </p>
      </section>
      )}

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)} title={confirm?.title}>
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg bg-[color-mix(in_oklab,var(--color-warning)_14%,transparent)] p-3 text-sm text-[color:var(--ink-base)]">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-[color:var(--color-accent-dark)] dark:text-[color:var(--color-warning)]" />
            <p>{confirm?.message}</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)}>Anulează</Button>
            <Button
              onClick={() => {
                confirm?.onConfirm();
                setConfirm(null);
              }}
            >
              Confirmă
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
